'use client';
import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import { CENTERED_ICON, COLORS, RADIUS, SHADOWS, SPACING } from '../../constants';
import { MaterialIcon, MaterialIconType } from '../materialIcon';

export type NoticeTone = 'success' | 'error' | 'info' | 'warning';

export const NOTICE_ACCENTS: Record<NoticeTone, { color: string; icon: MaterialIconType }> = {
  success: { color: COLORS.primary.green.dark, icon: 'check_circle' },
  error: { color: COLORS.state.error, icon: 'error' },
  info: { color: COLORS.primary.blue.main, icon: 'info' },
  warning: { color: COLORS.state.warning, icon: 'warning' },
};

/**
 * Floating card shared by toasts and the offline banner: white card, colored left edge and a tinted icon circle.
 *
 * @param {NoticeTone} tone - Picks the accent color and default icon.
 * @param {string} title - Main line.
 * @param {string} body - Optional secondary line.
 * @param {MaterialIconType} icon - Overrides the tone icon.
 * @param {React.ReactNode} action - Optional trailing control, e.g. a close button.
 */
export const NoticeCard = ({
  tone,
  title,
  body,
  icon,
  action,
  sx,
}: {
  tone: NoticeTone;
  title: string;
  body?: string;
  icon?: MaterialIconType;
  action?: React.ReactNode;
  sx?: SxProps<Theme>;
}) => {
  const accent = NOTICE_ACCENTS[tone];
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        gap: `${SPACING.sm}px`,
        py: `${SPACING.sm}px`,
        px: '14px',
        width: '100%',
        borderRadius: `${RADIUS.xl}px`,
        borderLeft: `4px solid ${accent.color}`,
        backgroundColor: COLORS.surface.raised,
        boxShadow: SHADOWS.floating,
        ...sx,
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: accent.color,
          backgroundColor: `${accent.color}1A`,
        }}
      >
        <MaterialIcon icon={icon ?? accent.icon} size={20} sx={CENTERED_ICON} />
      </Box>
      <Stack sx={{ flex: 1, gap: '2px', textAlign: 'left' }}>
        <Typography component="p" variant="body1" sx={{ fontWeight: 500, color: COLORS.text.main }}>
          {title}
        </Typography>
        {body && (
          <Typography component="p" variant="body2" sx={{ color: COLORS.text.light3 }}>
            {body}
          </Typography>
        )}
      </Stack>
      {action}
    </Stack>
  );
};
